// The software policy builder: what the page does with the account's software list and the winget catalog, apart from how it looks.
export const UPDATE_SCRIPT = 'cma/_v2/endpoint/software/winget/Update-WingetSoftware.ps1';
export const UNINSTALL_SCRIPT = 'cma/_v2/endpoint/software/Uninstall-Software.ps1';

const list = (v) => (Array.isArray(v) ? v : []);

// The software of the machines that match a rule set's filter: filter is its [{label, value}] pairs.
export const inventoryUrl = (account, filter, { cve = true } = {}) => {
  const query = { account };
  if (!cve) query.includeCve = 'false';                  // the CVE lookup is slow, and only the pages that show CVEs need it
  const pairs = list(filter).map((f) => `${f.label}=${f.value}`).join(';');
  if (pairs) query.filter = pairs;
  return `cma/softwareinventory?${new URLSearchParams(query).toString()}`;
};
export const searchUrl = (q) => `cma/wingetpackage?${new URLSearchParams({ q }).toString()}`;

// The key a program is known by in the policy: its definition's, else its winget id. Null when it has neither (it cannot be added yet).
export const programKey = (p) => p?.definition?.key || p?.winget?.id || null;

// The rule this rule set already has for a program, or null.
export const existingRule = (ruleSet, key) => list(ruleSet?.rules).find((r) => r.key === key) || null;

// A pattern that matches a name literally from its start; the page's way to make "Installed name matches" for a program it has seen.
export const escapePattern = (text) => String(text || '').replace(/[\\.^$*+?()[\]{}|]/g, '\\$&');
export const namePattern = (name) => `^${escapePattern(String(name || '').trim())}`;

// ---- the draft ------------------------------------------------------------------------------------------------------------
// An item: { key, name, wingetId, latestVersion, pattern, definitionExists, mode, keepUpdated, reason }.

export const itemFromPackage = (pkg, installed, mode = 'required') => ({
  key: installed?.definition?.key || pkg.id, name: installed?.definition?.displayName || pkg.name, wingetId: pkg.id, latestVersion: pkg.latestVersion || '',
  pattern: installed ? namePattern(installed.name) : pkg.suggestedPattern || namePattern(pkg.name), definitionExists: Boolean(installed?.definition), mode, reason: '',
});

// The definition to create for a program that has none: recognised by its pattern, updated by winget when it is in winget, and removed by
// Uninstall-Software (used only where a rule forbids it). This account only: a global definition is for the technicians to make.
export const definitionBody = (account, item) => ({
  account, kind: 'definition', key: item.key, displayName: item.name, note: '',
  detect: { namePattern: item.pattern, wingetId: item.wingetId },
  actions: {
    update: item.wingetId ? { script: UPDATE_SCRIPT, splat: { WingetId: item.wingetId }, dryRunSplat: {}, applySplat: {}, requiresApproval: false } : null,
    install: null,
    uninstall: { script: UNINSTALL_SCRIPT, splat: { Name: '{installedName}' }, dryRunSplat: {}, applySplat: {}, requiresApproval: false },
  },
});

const keepRules = (ruleSet) => list(ruleSet?.rules).map((r) => ({ key: r.key, mode: r.mode, reason: r.reason || '', enabled: r.enabled !== false }));

// The rule set to save: this rule set as it is, with the item's rule added or replacing the one it had for the program.
export const ruleSetBody = (account, ruleSet, item) => {
  const rules = keepRules(ruleSet);
  const rule = { key: item.key, mode: item.mode, reason: (item.reason || '').trim(), enabled: true };
  const at = rules.findIndex((r) => r.key === item.key);
  if (at >= 0) rules[at] = rule; else rules.push(rule);
  return { account, kind: 'ruleset', description: ruleSet.description || '', enabled: ruleSet.enabled !== false, filter: list(ruleSet.filter).map((f) => ({ label: f.label, value: f.value })), rules };
};

// The same rule set without its rule for the program.
export const removeRuleBody = (account, ruleSet, key) => ({
  account, kind: 'ruleset', description: ruleSet.description || '', enabled: ruleSet.enabled !== false,
  filter: list(ruleSet.filter).map((f) => ({ label: f.label, value: f.value })), rules: keepRules(ruleSet).filter((r) => r.key !== key),
});

// ---- the policy as it stands ----------------------------------------------------------------------------------------------

const MODE_ORDER = { required: 0, forbidden: 1, allowed: 2 };

// The rows of the Policy tab: each rule of the rule set with the program it names, and what it finds today on the machines that match.
// definitions: the account's program definitions; programs: the installed software (may be empty while it is still loading); machines: how
// many machines match and have reported.
export const policyRows = (ruleSet, definitions, programs, machines) => {
  const byKey = new Map(list(programs).map((p) => [programKey(p), p]));
  return list(ruleSet?.rules).map((rule) => {
    const program = byKey.get(rule.key) || null;
    const definition = list(definitions).find((d) => d.key === rule.key) || program?.definition || null;
    const have = program ? program.machineCount : 0;
    return {
      key: rule.key, mode: rule.mode, reason: rule.reason || '', enabled: rule.enabled !== false, program, definition,
      name: definition?.displayName || program?.name || rule.key,
      wingetId: definition?.detect?.wingetId || definition?.wingetId || program?.winget?.id || null,
      have,
      missing: rule.mode === 'required' ? Math.max(0, machines - have) : 0,
      present: rule.mode === 'forbidden' ? have : 0,
      outdated: rule.mode !== 'forbidden' && program?.updateAvailable ? have : 0,
    };
  }).sort((a, b) => (MODE_ORDER[a.mode] ?? 9) - (MODE_ORDER[b.mode] ?? 9) || a.name.localeCompare(b.name));
};

// "4 rules: 2 required, 1 allowed, 1 forbidden" and what they find today.
export const policySummary = (rows) => ({
  rules: rows.length,
  required: rows.filter((r) => r.mode === 'required').length,
  allowed: rows.filter((r) => r.mode === 'allowed').length,
  forbidden: rows.filter((r) => r.mode === 'forbidden').length,
  missing: rows.reduce((n, r) => n + r.missing, 0),
  present: rows.reduce((n, r) => n + r.present, 0),
  outdated: rows.reduce((n, r) => n + r.outdated, 0),
});
